import React from 'react';
import MarkdownText from 'MarkdownText';
import useIsMobile from 'hooks/useIsMobile';
import {
  pickDishAction, orderLinkTarget,
  ORDER_INLINE, ORDER_CONTINUE, ORDER_LINK, RESERVE_LINK,
} from 'cards/orderSurface';

// Visual variety for dish images, applied at render time so the same
// underlying PNG can read very differently depending on the card.
//
// Four independent palettes — filter, height, crop position, rotation —
// each hashed off a *different* dimension so the picks aren't correlated
// (a card isn't both warm-sepia AND tilted AND tall AND top-cropped just
// because its dish_id is high). Default-leaning entries are duplicated
// in some palettes so "no transform" stays the most common case.
const IMAGE_FILTERS = [
  'none',                                          // unmodified
  'sepia(0.30) saturate(1.10)',                    // warm vintage
  'hue-rotate(-10deg) saturate(1.20)',             // slightly cooler
  'contrast(1.15) saturate(1.05)',                 // punchier
  'brightness(1.08) contrast(0.95) saturate(0.95)',// faded soft
  'brightness(0.82) contrast(1.10)',               // moody dim
  'sepia(0.15) brightness(0.92) saturate(1.05)',   // soft golden
  'saturate(0.75) contrast(1.05)',                 // muted
];

// minHeight values shift the card's vertical proportions slightly so
// some look chunkier, some slimmer. The default appears multiple times
// so most cards stay close to the reference shape.
const IMAGE_HEIGHTS = ['100px', '100px', '120px', '140px', '90px'];

// Crop framing — most cards center, a few favor top/bottom/left/right
// of the source image so the same photo reads differently from card to card.
const OBJECT_POSITIONS = [
  'center', 'center', 'center top', 'center bottom', 'left center', 'right center',
];

// Slight rotation à la polaroid for a small fraction of cards. 0deg
// appears multiple times so most cards stay straight.
const ROTATIONS = ['0deg', '0deg', '0deg', '-1.2deg', '1.2deg', '-2deg', '2deg'];

// Very light tints — readable but distinct so a column of aggregator
// results visually separates per restaurant. Same hashing as other
// palettes: stable within a session, varies per (station, dish).
const CARD_BACKGROUNDS = [
  '#fef2f2', // rose
  '#fff7ed', // orange
  '#fefce8', // yellow
  '#f7fee7', // lime
  '#f0fdf4', // green
  '#ecfeff', // cyan
  '#eff6ff', // blue
  '#eef2ff', // indigo
  '#f5f3ff', // violet
  '#fdf4ff', // fuchsia
  '#fdf2f8', // pink
  '#f8fafc', // slate (close to original f8f9fa)
];

// Session-level random salt: changes on every page reload so the same
// dish renders a (possibly) different way across sessions. Within one
// session, all picks remain stable so a re-query of the same dish
// produces the same card.
const SESSION_SALT = Math.random().toString(36).slice(2, 10);

function _hashIndex(seed, modulo) {
  // Tiny FNV-1a — stable across browsers, fast, no dependencies.
  let h = 2166136261;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = (h * 16777619) >>> 0;
  }
  return h % modulo;
}

function _pick(structured, dimension, palette) {
  const sid = structured.source_station_id ?? structured?.action?.station?.id ?? '';
  const did = structured?.action?.prefilled?.dish_id ?? '';
  const key = `${SESSION_SALT}:${dimension}:${sid}:${did}`;
  return palette[_hashIndex(key, palette.length)];
}

// Card for `kind: "tobin.dish"` structured values. Domain-specific layout
// (text + Claim button on the left ~2/3, dish image on the right ~1/3)
// lives entirely in here so the generic chat surface stays domain-blind.
//
// Expected structured fields:
//   text       — markdown body (restaurant/statement/score/dish line)
//   action     — Claim action spec (button rendered, onAction propagated)
//   image_url  — optional; right column hidden if missing or load fails
export default function TobinDishCard({ structured, onAction }) {
  const isMobile = useIsMobile();
  // in-station-ordering: one click per render — disable on click; the reply
  // (prepare_order → order card/text) lands in this same chat.
  const [ordering, setOrdering] = React.useState(false);
  const imageFilter = _pick(structured, 'filter', IMAGE_FILTERS);
  const imageMinHeight = _pick(structured, 'height', IMAGE_HEIGHTS);
  const imageObjectPos = _pick(structured, 'crop', OBJECT_POSITIONS);
  const imageRotation = _pick(structured, 'rot', ROTATIONS);
  const cardBackground = _pick(structured, 'bg', CARD_BACKGROUNDS);
  // tobin-order-at-seller / tobin-reservations: which affordance this render
  // gets — Reserve, Order, or nothing (see the comment on the button below,
  // and cards/orderSurface.js for the rule).
  const orderSurface = pickDishAction(structured, onAction);
  // Reserve and Order (away from the seller) are the card's built-in Continue actions.
  const continueAct = orderSurface === RESERVE_LINK ? structured.reserve_continue
    : orderSurface === ORDER_CONTINUE ? structured.order_continue : null;
  // On mobile, flip from 2:1 horizontal (text|image) to vertical banner
  // stack — image as a full-width header on top, text + button below.
  // Side-by-side at ~400px viewport cramps both halves (text reads
  // narrow, image too small to function as the appetite-cue + visual-
  // variety hook the filter/crop/rotation code exists for).
  const textColStyle = isMobile
    ? { width: '100%' }
    : { flex: '2 1 0', minWidth: 0 };
  const imageColStyle = isMobile
    // order:-1 places the image before the text in the flex stack
    // without swapping JSX, keeping the 404-hide handler simple.
    ? { width: '100%', order: -1 }
    : { flex: '1 1 0', minWidth: 0, alignSelf: 'stretch' };
  return (
    <div style={{
      marginBottom: '10px', padding: '10px', background: cardBackground,
      borderRadius: '8px', border: '1px solid #e9ecef',
      display: 'flex', gap: '10px', alignItems: 'stretch',
      flexDirection: isMobile ? 'column' : 'row',
    }}>
      <div style={textColStyle}>
        <div style={{ fontSize: '0.9rem', fontFamily: '"IBM Plex Mono", monospace', marginBottom: '8px' }}>
          <MarkdownText text={structured.text} />
        </div>
        {/* tobin-reservations: today's service only. A reader looking at one
            card is asking whether they can go tonight; the full week belongs
            in the restaurant's own chat, which is one click away. Absent when
            the restaurant has published no hours — saying nothing is right
            there, where claiming "closed" would be a lie. */}
        {structured.hours_today && (
          <div style={{ fontSize: '0.72rem', color: '#6c757d', marginBottom: '8px' }}>
            {structured.hours_today === 'closed today'
              ? 'Closed today'
              : `Today: ${structured.hours_today}`}
          </div>
        )}
        {/* Order always lands the buyer in the seller's own chat. Which button
            gets them there is cards/orderSurface.js; only an Order already in
            that chat dispatches in-thread. */}
        {orderSurface === ORDER_INLINE ? (
          <button
            disabled={ordering}
            onClick={() => { setOrdering(true); onAction(structured.order_action, structured); }}
            style={{
              fontSize: '0.75rem', fontWeight: 700, padding: '4px 12px',
              border: ordering ? '1px solid #cbd5e1' : '1px solid #0084ff',
              borderRadius: '14px', background: 'transparent',
              color: ordering ? '#94a3b8' : '#0084ff',
              cursor: ordering ? 'default' : 'pointer',
            }}
          >
            {ordering ? 'Starting…' : `Order${structured.price_display ? ` — ${structured.price_display}` : ''}`}
          </button>
        ) : continueAct ? (
          /* Opens the restaurant's chat with this dish in context: Reserve prefills
             the opening line, Order starts the order on arrival. */
          <button
            onClick={() => onAction && onAction(continueAct, structured)}
            style={{
              fontSize: '0.75rem', fontWeight: 700, padding: '4px 12px',
              border: '1px solid #0084ff', borderRadius: '14px', background: 'transparent',
              color: '#0084ff', cursor: 'pointer',
            }}
          >
            {continueAct.label}
          </button>
        ) : orderSurface === ORDER_LINK && (
          <a
            href={structured.order_url}
            target={orderLinkTarget()}
            rel="noopener noreferrer"
            style={{
              display: 'inline-block', fontSize: '0.75rem', fontWeight: 700,
              padding: '4px 12px', border: '1px solid #0084ff',
              borderRadius: '14px', background: 'transparent',
              color: '#0084ff', textDecoration: 'none', cursor: 'pointer',
            }}
          >
            Order{structured.price_display ? ` — ${structured.price_display}` : ''}
          </a>
        )}
      </div>
      {structured.image_url && (
        // Image column. Desktop: right side, ~1/3 width. Mobile: full-
        // width banner above the text (via flex order:-1). On 404/load
        // failure the wrapper hides itself and the text reflows.
        <div style={imageColStyle}>
          <img
            src={structured.image_url}
            alt=""
            style={{
              width: '100%', height: '100%', objectFit: 'cover',
              borderRadius: '6px', display: 'block',
              minHeight: imageMinHeight,
              filter: imageFilter,
              objectPosition: imageObjectPos,
              transform: `rotate(${imageRotation})`,
            }}
            onError={(e) => {
              const wrapper = e.currentTarget.parentElement;
              if (wrapper) wrapper.style.display = 'none';
            }}
          />
        </div>
      )}
    </div>
  );
}
